// Notification Bell Component with Real-time Updates
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, BellRing, Check, CheckCheck, Trash2, X, 
  AlertCircle, Info, FileText, Calendar, Activity,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useData';
import { fileService } from '@/services/fileService';
import { formatDistanceToNow } from 'date-fns';

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'alert':
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    case 'document':
      return <FileText className="h-4 w-4 text-blue-500" />;
    case 'appointment':
      return <Calendar className="h-4 w-4 text-purple-500" />;
    case 'vital':
    case 'health':
      return <Activity className="h-4 w-4 text-green-500" />;
    default:
      return <Info className="h-4 w-4 text-gray-500" />;
  }
};

const formatTime = (dateString) => {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
};

export const NotificationBell = ({ className }) => {
  const [open, setOpen] = useState(false);
  const { notifications, loading, refetch, markAsRead } = useNotifications();
  const [localNotifications, setLocalNotifications] = useState([]);
  
  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);
  
  const unreadCount = localNotifications.filter(n => !n.read).length;
  
  const handleMarkAsRead = async (notificationId) => {
    setLocalNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    try {
      const { markAsRead: markRead } = await import('@/services/notificationService');
      await markRead(notificationId);
    } catch {
      await fileService.markNotificationRead(notificationId);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    setLocalNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const { markAllRead } = await import('@/services/notificationService');
      await markAllRead();
    } catch {
      for (const n of localNotifications.filter(n => !n.read)) {
        await fileService.markNotificationRead(n.id);
      }
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
        >
          <AnimatePresence mode="wait">
            {unreadCount > 0 ? (
              <motion.div
                key="ringing"
                initial={{ rotate: -15 }}
                animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                <BellRing className="h-5 w-5" />
              </motion.div>
            ) : (
              <Bell className="h-5 w-5" />
            )}
          </AnimatePresence>
          
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-[min(22rem,calc(100vw-1rem))] p-0" align="end">
        <div className="flex items-center justify-between border-b border-border/70 bg-muted/30 p-4">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[min(400px,calc(100dvh-12rem))]">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : localNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-border/70">
              {localNotifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={cn(
                    "p-4 transition-colors cursor-pointer hover:bg-muted/50",
                    !notification.read && "bg-clinical-soft/45"
                  )}
                  onClick={() => handleMarkAsRead(notification.id)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <NotificationIcon type={notification.type} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          "text-sm",
                          !notification.read && "font-medium"
                        )}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-clinical flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {localNotifications.length > 0 && (
          <div className="border-t border-border/70 bg-muted/20 p-2">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => {
                setOpen(false);
                // Navigate to notifications page if exists
              }}
            >
              View all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
